// WhatIfSimulator — Interactive what-if simulation workspace
// Variable panel with discoverable pickers + impact dashboard + line item results table
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  InputLabel,
  FormControl,
  Tooltip,
  Autocomplete,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { bomIntelligenceService } from '../../../services/bomIntelligenceService';
import bomV2Service from '../../../services/bomV2Service';
import { suppliersService } from '../../../services/suppliersService';
import type {
  VariableAdjustment,
  WhatIfSimulationResponse,
} from '../../../services/bomIntelligenceService';
import type {
  PartSupplier,
  AlternativesLineItem,
  AlternativeMatch,
} from '../../../types';

interface WhatIfSimulatorProps {
  bomId: string;
  onViewPart?: (partId: string) => void;
}

// --- Picker option types ---

interface LineItemOption {
  lineItemId: string;
  lineNumber: number;
  partId: string;
  partNumber: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

interface SupplierOption {
  supplierId: string;
  name: string;
  unitPrice?: number;
  leadTimeDays?: number;
  isPreferred?: boolean;
  stockStatus?: string;
}

interface AlternativePartOption {
  alternativePartId: string;
  alternativePartNumber: string;
  description: string;
  fffScore: number;
  costDifference?: number;
  source: string;
  confidence: number;
}

// --- Adjustment Row ---

interface AdjustmentRow {
  id: string;
  type: VariableAdjustment['type'];
  scope: VariableAdjustment['scope'];
  target: string;
  value: number;
  // For swap types
  lineItemId: string;
  newSupplierId: string;
  newPartId: string;
  // Display labels (for selected options)
  _lineItemLabel: string;
  _supplierLabel: string;
  _partLabel: string;
}

const ADJUSTMENT_TYPES = [
  { value: 'PRICE_PERCENT', label: 'Price (%)', group: 'Price' },
  { value: 'PRICE_ABSOLUTE', label: 'Price ($)', group: 'Price' },
  { value: 'PRICE_SET', label: 'Set Price', group: 'Price' },
  { value: 'QUANTITY_MULTIPLIER', label: 'Quantity (x)', group: 'Quantity' },
  { value: 'LEAD_TIME_PERCENT', label: 'Lead Time (%)', group: 'Lead Time' },
  { value: 'LABOR_RATE', label: 'Labor Rate ($/hr)', group: 'Global' },
  { value: 'OVERHEAD_PERCENT', label: 'Overhead (%)', group: 'Global' },
  { value: 'SUPPLIER_SWAP', label: 'Swap Supplier', group: 'Swap' },
  { value: 'PART_SWAP', label: 'Swap Part', group: 'Swap' },
  { value: 'SUPPLIER_DISABLE', label: 'Disable Supplier', group: 'Disable' },
] as const;

const isSwapType = (type: string) => type === 'SUPPLIER_SWAP' || type === 'PART_SWAP';
const isDisableType = (type: string) => type === 'SUPPLIER_DISABLE';
const isGlobalType = (type: string) => type === 'LABOR_RATE' || type === 'OVERHEAD_PERCENT';

const SCOPE_OPTIONS = [
  { value: 'GLOBAL', label: 'All Items' },
  { value: 'MATERIAL_CATEGORY', label: 'Material Category' },
  { value: 'ITEM', label: 'Specific Item' },
  { value: 'SUPPLIER', label: 'Specific Supplier' },
] as const;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

let nextId = 1;

export const WhatIfSimulator: React.FC<WhatIfSimulatorProps> = ({
  bomId,
  onViewPart,
}) => {
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WhatIfSimulationResponse | null>(null);
  const [saveName, setSaveName] = useState('');

  // --- BOM context data (loaded once) ---
  const [bomLineItems, setBomLineItems] = useState<LineItemOption[]>([]);
  const [alternativesMap, setAlternativesMap] = useState<Record<string, AlternativePartOption[]>>({});
  const [uniqueSuppliers, setUniqueSuppliers] = useState<SupplierOption[]>([]);
  const [contextLoading, setContextLoading] = useState(true);

  // Per-lineItem supplier cache (loaded lazily)
  const [supplierCache, setSupplierCache] = useState<Record<string, SupplierOption[]>>({});
  const [supplierLoading, setSupplierLoading] = useState<Record<string, boolean>>({});

  // --- Load BOM context on mount ---
  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      setContextLoading(true);
      try {
        // Load BOM overview (header + tree + active-variant line-variants in
        // one round-trip) and alternatives in parallel. Overview gives the
        // line items as a flat array; per-line picked unit price comes from
        // the active variant's lineVariants when set, otherwise we fall back
        // to the cheapest cost-mode candidate so the picker still has a price.
        const [overview, alternativesData] = await Promise.all([
          bomV2Service.getOverview(bomId),
          bomIntelligenceService.getAlternativesIntelligence(bomId).catch(() => null),
        ]);

        if (cancelled) return;

        const lineVariantById = new Map(
          (overview.lineVariants || []).map((lv) => [lv.lineItemId, lv]),
        );
        const tree = overview.tree || [];

        const items: LineItemOption[] = tree
          // Simulation needs a costed part — assemblies and DOCUMENT lines
          // (customer-package process specs, no partId) are out of scope.
          .filter((li) => li.type !== 'ASSEMBLY' && li.type !== 'DOCUMENT' && !!li.partId)
          .map((li) => {
            const lv = lineVariantById.get(li.lineItemId);
            const pickedId = lv?.supplier?.partSupplierId;
            const candidates = li.candidates || [];
            const picked = pickedId
              ? candidates.find((c) => c.partSupplierId === pickedId)
              : undefined;
            const cheapest = candidates
              .filter((c) => typeof c.unitPrice === 'number' && c.unitPrice > 0)
              .sort((a, b) => (a.unitPrice ?? 0) - (b.unitPrice ?? 0))[0];
            const unitPrice = picked?.unitPrice ?? cheapest?.unitPrice ?? 0;
            return {
              lineItemId: li.lineItemId,
              lineNumber: li.lineNumber ?? 0,
              // Guaranteed by the partId filter above; TS can't narrow through .filter().
              partId: li.partId!,
              partNumber: li.partNumber || `Item #${li.lineNumber ?? ''}`,
              description: li.partDescription || '',
              quantity: li.quantity,
              unitPrice,
            };
          });
        setBomLineItems(items);

        const supplierMap = new Map<string, SupplierOption>();
        for (const li of tree) {
          if (li.manufacturerName) {
            supplierMap.set(li.manufacturerName, {
              supplierId: li.manufacturerName,
              name: li.manufacturerName,
            });
          }
        }
        setUniqueSuppliers(Array.from(supplierMap.values()));

        // Build alternatives map: lineItemId -> alternative parts
        if (alternativesData?.lineItemAlternatives) {
          const altMap: Record<string, AlternativePartOption[]> = {};
          for (const la of alternativesData.lineItemAlternatives) {
            if (la.alternatives && la.alternatives.length > 0) {
              altMap[la.lineItemId] = la.alternatives.map((a: AlternativeMatch) => ({
                alternativePartId: a.alternativePartId,
                alternativePartNumber: a.alternativePartNumber,
                description: a.alternativeDescription || '',
                fffScore: a.fffScore,
                costDifference: a.costDifference,
                source: a.source,
                confidence: a.confidence,
              }));
            }
          }
          setAlternativesMap(altMap);
        }
      } catch {
        // Non-critical — pickers just won't have options
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    };

    loadContext();
    return () => { cancelled = true; };
  }, [bomId]);

  // --- Load suppliers for a specific part (lazy) ---
  const loadSuppliersForPart = useCallback(async (partId: string) => {
    if (!partId || supplierCache[partId] || supplierLoading[partId]) return;

    setSupplierLoading((prev) => ({ ...prev, [partId]: true }));
    try {
      const suppliers: PartSupplier[] = await suppliersService.getByPartId(partId);
      const options: SupplierOption[] = suppliers.map((ps) => ({
        supplierId: ps.supplierId || ps.supplier?._id || ps._id,
        name: ps.supplier?.name || ps.supplierId || 'Unknown',
        unitPrice: ps.unitPrice,
        leadTimeDays: ps.leadTimeDays,
        isPreferred: ps.isPreferredForPart,
      }));
      setSupplierCache((prev) => ({ ...prev, [partId]: options }));
    } catch {
      setSupplierCache((prev) => ({ ...prev, [partId]: [] }));
    } finally {
      setSupplierLoading((prev) => ({ ...prev, [partId]: false }));
    }
  }, [supplierCache, supplierLoading]);

  // --- Adjustment management ---

  const addAdjustment = useCallback(() => {
    setAdjustments((prev) => [
      ...prev,
      {
        id: `adj-${nextId++}`, type: 'PRICE_PERCENT', scope: 'GLOBAL',
        target: '', value: 0, lineItemId: '', newSupplierId: '', newPartId: '',
        _lineItemLabel: '', _supplierLabel: '', _partLabel: '',
      },
    ]);
  }, []);

  const removeAdjustment = useCallback((id: string) => {
    setAdjustments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const updateAdjustment = useCallback((id: string, updates: Partial<AdjustmentRow>) => {
    setAdjustments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    );
  }, []);

  const resetAll = useCallback(() => {
    setAdjustments([]);
    setResult(null);
    setError(null);
  }, []);

  // --- Run simulation ---

  const runSimulation = useCallback(async () => {
    if (adjustments.length === 0) {
      setError('Add at least one variable adjustment to run a simulation.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dtoAdjustments: VariableAdjustment[] = adjustments.map((a) => {
        if (isSwapType(a.type)) {
          return {
            type: a.type,
            scope: 'ITEM' as const,
            lineItemId: a.lineItemId || undefined,
            newSupplierId: a.type === 'SUPPLIER_SWAP' ? (a.newSupplierId || undefined) : undefined,
            newPartId: a.type === 'PART_SWAP' ? (a.newPartId || undefined) : undefined,
          };
        }
        if (isDisableType(a.type)) {
          return {
            type: a.type,
            scope: 'SUPPLIER' as const,
            target: a.target || undefined,
          };
        }
        return {
          type: a.type,
          scope: isGlobalType(a.type) ? 'GLOBAL' as const : a.scope,
          target: a.target || undefined,
          value: a.value,
        };
      });

      const response = await bomIntelligenceService.runWhatIfSimulation({
        bomId,
        adjustments: dtoAdjustments,
      });

      setResult(response);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [bomId, adjustments]);

  // --- Save simulation ---

  const saveSimulation = useCallback(async () => {
    if (!result || !saveName.trim()) return;

    try {
      await bomIntelligenceService.saveSimulation({
        simulationId: result.simulationId,
        name: saveName.trim(),
      });
      setSaveName('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save simulation');
    }
  }, [result, saveName]);

  // --- Render ---

  return (
    <Box>
      {/* Variable Panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Variable Adjustments
            </Typography>
            {contextLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading BOM data...
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addAdjustment}
              variant="outlined"
            >
              Add Variable
            </Button>
            {adjustments.length > 0 && (
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={resetAll}
                color="warning"
              >
                Reset All
              </Button>
            )}
          </Stack>
        </Box>

        {adjustments.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            Click "Add Variable" to define price, quantity, or supplier adjustments.
            Then click "Run Simulation" to see the impact on cost and readiness.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {adjustments.map((adj) => (
              <AdjustmentRowComponent
                key={adj.id}
                adj={adj}
                bomLineItems={bomLineItems}
                alternativesMap={alternativesMap}
                uniqueSuppliers={uniqueSuppliers}
                supplierCache={supplierCache}
                supplierLoading={supplierLoading}
                onUpdate={updateAdjustment}
                onRemove={removeAdjustment}
                onLoadSuppliers={loadSuppliersForPart}
              />
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={runSimulation}
            disabled={loading || adjustments.length === 0}
          >
            {loading ? 'Running...' : 'Run Simulation'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Impact Dashboard */}
      {result && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Impact Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <MetricCard
                label="Total Cost"
                baseline={result.baselineMetrics.totalCost}
                simulated={result.simulatedMetrics.totalCost}
                delta={result.simulatedMetrics.deltaCost}
                deltaPercent={result.simulatedMetrics.deltaCostPercent}
                format="currency"
                invertColor
              />
              <MetricCard
                label="Readiness Score"
                baseline={result.baselineMetrics.readinessScore}
                simulated={result.simulatedMetrics.readinessScore}
                delta={result.simulatedMetrics.deltaReadiness}
                format="percent"
              />
              <MetricCard
                label="Avg Lead Time"
                baseline={result.baselineMetrics.avgLeadTimeDays}
                simulated={result.simulatedMetrics.avgLeadTimeDays}
                delta={result.simulatedMetrics.avgLeadTimeDays - result.baselineMetrics.avgLeadTimeDays}
                format="days"
                invertColor
              />
              <MetricCard
                label="At-Risk Items"
                baseline={result.baselineMetrics.atRiskCount}
                simulated={result.simulatedMetrics.atRiskCount}
                delta={result.simulatedMetrics.atRiskCount - result.baselineMetrics.atRiskCount}
                format="count"
                invertColor
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Save controls */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Simulation Name"
                placeholder="e.g., Material Price Spike +15%"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={saveSimulation}
                disabled={!saveName.trim()}
              >
                Save
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Computed in {result.durationMs}ms
              </Typography>
            </Box>
          </Paper>

          {/* Line Item Results */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Line Item Impact ({result.lineItemResults.filter((r) => r.changed).length} items changed)
            </Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Part Number</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Original Cost</TableCell>
                    <TableCell align="right">Simulated Cost</TableCell>
                    <TableCell align="right">Delta</TableCell>
                    <TableCell>Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.lineItemResults.map((item) => (
                    <TableRow
                      key={item.lineItemId}
                      sx={{
                        bgcolor: item.changed ? 'action.hover' : undefined,
                      }}
                    >
                      <TableCell>{item.lineNumber}</TableCell>
                      <TableCell>
                        {item.partId && onViewPart ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => onViewPart(item.partId)}
                          >
                            {item.partNumber || item.partId}
                          </Typography>
                        ) : (
                          <Typography variant="body2">{item.partNumber || '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.originalTotalCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.simulatedTotalCost)}</TableCell>
                      <TableCell align="right">
                        {item.costDelta !== 0 ? (
                          <Typography
                            variant="body2"
                            color={item.costDelta > 0 ? 'error.main' : 'success.main'}
                            fontWeight={500}
                          >
                            {item.costDelta > 0 ? '+' : ''}{formatCurrency(item.costDelta)}
                            <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                              ({item.costDeltaPercent > 0 ? '+' : ''}{item.costDeltaPercent.toFixed(1)}%)
                            </Typography>
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.changed && item.changeReason ? (
                          <Chip label={item.changeReason} size="small" color="info" variant="outlined" />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

// ============================================
// ADJUSTMENT ROW COMPONENT
// ============================================

interface AdjustmentRowProps {
  adj: AdjustmentRow;
  bomLineItems: LineItemOption[];
  alternativesMap: Record<string, AlternativePartOption[]>;
  uniqueSuppliers: SupplierOption[];
  supplierCache: Record<string, SupplierOption[]>;
  supplierLoading: Record<string, boolean>;
  onUpdate: (id: string, updates: Partial<AdjustmentRow>) => void;
  onRemove: (id: string) => void;
  onLoadSuppliers: (partId: string) => void;
}

const AdjustmentRowComponent: React.FC<AdjustmentRowProps> = ({
  adj,
  bomLineItems,
  alternativesMap,
  uniqueSuppliers,
  supplierCache,
  supplierLoading,
  onUpdate,
  onRemove,
  onLoadSuppliers,
}) => {
  // Resolve selected line item option
  const selectedLineItem = useMemo(
    () => bomLineItems.find((li) => li.lineItemId === adj.lineItemId) || null,
    [bomLineItems, adj.lineItemId],
  );

  // Trigger supplier load when a line item is selected for SUPPLIER_SWAP
  useEffect(() => {
    if (adj.type === 'SUPPLIER_SWAP' && selectedLineItem?.partId) {
      onLoadSuppliers(selectedLineItem.partId);
    }
  }, [adj.type, selectedLineItem?.partId, onLoadSuppliers]);

  // Suppliers for the selected part
  const partSuppliers = selectedLineItem?.partId
    ? (supplierCache[selectedLineItem.partId] || [])
    : [];
  const isLoadingSuppliers = selectedLineItem?.partId
    ? (supplierLoading[selectedLineItem.partId] || false)
    : false;

  // Alternatives for the selected line item
  const lineAlternatives = adj.lineItemId
    ? (alternativesMap[adj.lineItemId] || [])
    : [];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        flexWrap: 'wrap',
      }}
    >
      {/* Type selector */}
      <FormControl size="small" sx={{ minWidth: 155 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={adj.type}
          label="Type"
          onChange={(e) => onUpdate(adj.id, {
            type: e.target.value as AdjustmentRow['type'],
            // Reset dependent fields when type changes
            lineItemId: '', newSupplierId: '', newPartId: '', target: '',
            _lineItemLabel: '', _supplierLabel: '', _partLabel: '',
            scope: isGlobalType(e.target.value) ? 'GLOBAL' : adj.scope,
          })}
        >
          {ADJUSTMENT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t.group === 'Swap' && <SwapHorizIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                {t.label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* === SUPPLIER_SWAP: Line Item picker + Supplier picker === */}
      {adj.type === 'SUPPLIER_SWAP' && (
        <>
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={bomLineItems}
            value={selectedLineItem}
            onChange={(_, opt) => {
              onUpdate(adj.id, {
                lineItemId: opt?.lineItemId || '',
                _lineItemLabel: opt ? `#${opt.lineNumber} ${opt.partNumber}` : '',
                // Reset supplier when part changes
                newSupplierId: '', _supplierLabel: '',
              });
            }}
            getOptionLabel={(opt) => `#${opt.lineNumber} ${opt.partNumber}`}
            renderOption={(props, opt) => (
              <li {...props} key={opt.lineItemId}>
                <ListItemText
                  primary={`#${opt.lineNumber} — ${opt.partNumber}`}
                  secondary={`${opt.description ? opt.description.slice(0, 50) : ''} | Qty: ${opt.quantity} | ${formatCurrency(opt.unitPrice)}/ea`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Part" placeholder="Search parts..." />
            )}
            isOptionEqualToValue={(opt, val) => opt.lineItemId === val.lineItemId}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={partSuppliers}
            loading={isLoadingSuppliers}
            disabled={!selectedLineItem}
            value={partSuppliers.find((s) => s.supplierId === adj.newSupplierId) || null}
            onChange={(_, opt) => {
              onUpdate(adj.id, {
                newSupplierId: opt?.supplierId || '',
                _supplierLabel: opt?.name || '',
              });
            }}
            getOptionLabel={(opt) => opt.name}
            renderOption={(props, opt) => (
              <li {...props} key={opt.supplierId}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {opt.name}
                      {opt.isPreferred && (
                        <Chip label="Preferred" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Box>
                  }
                  secondary={`${opt.unitPrice != null ? formatCurrency(opt.unitPrice) + '/ea' : 'No price'}${opt.leadTimeDays != null ? ` | ${opt.leadTimeDays}d lead` : ''}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Swap to Supplier"
                placeholder={!selectedLineItem ? 'Select a part first' : 'Search suppliers...'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingSuppliers && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={isLoadingSuppliers ? 'Loading suppliers...' : 'No other suppliers found'}
            isOptionEqualToValue={(opt, val) => opt.supplierId === val.supplierId}
          />
        </>
      )}

      {/* === PART_SWAP: Line Item picker + Alternative Part picker === */}
      {adj.type === 'PART_SWAP' && (
        <>
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={bomLineItems}
            value={selectedLineItem}
            onChange={(_, opt) => {
              onUpdate(adj.id, {
                lineItemId: opt?.lineItemId || '',
                _lineItemLabel: opt ? `#${opt.lineNumber} ${opt.partNumber}` : '',
                // Reset alt part when line item changes
                newPartId: '', _partLabel: '',
              });
            }}
            getOptionLabel={(opt) => `#${opt.lineNumber} ${opt.partNumber}`}
            renderOption={(props, opt) => {
              const hasAlts = (alternativesMap[opt.lineItemId]?.length || 0) > 0;
              return (
                <li {...props} key={opt.lineItemId}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {`#${opt.lineNumber} — ${opt.partNumber}`}
                        {hasAlts && (
                          <Chip
                            label={`${alternativesMap[opt.lineItemId].length} alt`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={`${opt.description ? opt.description.slice(0, 50) : ''} | ${formatCurrency(opt.unitPrice)}/ea`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Part" placeholder="Search parts..." />
            )}
            isOptionEqualToValue={(opt, val) => opt.lineItemId === val.lineItemId}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={lineAlternatives}
            disabled={!selectedLineItem}
            value={lineAlternatives.find((a) => a.alternativePartId === adj.newPartId) || null}
            onChange={(_, opt) => {
              onUpdate(adj.id, {
                newPartId: opt?.alternativePartId || '',
                _partLabel: opt?.alternativePartNumber || '',
              });
            }}
            getOptionLabel={(opt) => opt.alternativePartNumber}
            renderOption={(props, opt) => (
              <li {...props} key={opt.alternativePartId}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {opt.alternativePartNumber}
                      <Chip
                        label={`FFF ${(opt.fffScore * 100).toFixed(0)}%`}
                        size="small"
                        color={opt.fffScore >= 0.8 ? 'success' : opt.fffScore >= 0.6 ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    </Box>
                  }
                  secondary={`${opt.description ? opt.description.slice(0, 40) : opt.source}${opt.costDifference != null ? ` | ${opt.costDifference < 0 ? 'Saves' : 'Adds'} ${formatCurrency(Math.abs(opt.costDifference))}/ea` : ''} | Confidence: ${(opt.confidence * 100).toFixed(0)}%`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Swap to Part"
                placeholder={!selectedLineItem ? 'Select a part first' : lineAlternatives.length === 0 ? 'No alternatives found' : 'Search alternatives...'}
              />
            )}
            noOptionsText="No alternatives found for this part"
            isOptionEqualToValue={(opt, val) => opt.alternativePartId === val.alternativePartId}
          />
        </>
      )}

      {/* === SUPPLIER_DISABLE: Supplier picker === */}
      {isDisableType(adj.type) && (
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={uniqueSuppliers}
          freeSolo
          value={uniqueSuppliers.find((s) => s.supplierId === adj.target) || (adj.target ? { supplierId: adj.target, name: adj.target } as SupplierOption : null)}
          onChange={(_, opt) => {
            if (typeof opt === 'string') {
              onUpdate(adj.id, { target: opt });
            } else {
              onUpdate(adj.id, { target: opt?.supplierId || '' });
            }
          }}
          getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
          renderInput={(params) => (
            <TextField {...params} label="Supplier to Disable" placeholder="Select or type supplier..." />
          )}
          isOptionEqualToValue={(opt, val) => {
            if (typeof opt === 'string' || typeof val === 'string') return opt === val;
            return opt.supplierId === val.supplierId;
          }}
        />
      )}

      {/* === Standard types: scope + target + value === */}
      {!isSwapType(adj.type) && !isDisableType(adj.type) && (
        <>
          {!isGlobalType(adj.type) && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Scope</InputLabel>
              <Select
                value={adj.scope}
                label="Scope"
                onChange={(e) => onUpdate(adj.id, {
                  scope: e.target.value as AdjustmentRow['scope'],
                  target: '',
                })}
              >
                {SCOPE_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Item scope -> Autocomplete from BOM line items */}
          {adj.scope === 'ITEM' && !isGlobalType(adj.type) && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={bomLineItems}
              value={bomLineItems.find((li) => li.lineItemId === adj.target) || null}
              onChange={(_, opt) => {
                onUpdate(adj.id, { target: opt?.lineItemId || '' });
              }}
              getOptionLabel={(opt) => `#${opt.lineNumber} ${opt.partNumber}`}
              renderOption={(props, opt) => (
                <li {...props} key={opt.lineItemId}>
                  <ListItemText
                    primary={`#${opt.lineNumber} — ${opt.partNumber}`}
                    secondary={opt.description?.slice(0, 40) || ''}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Target Item" placeholder="Search items..." />
              )}
              isOptionEqualToValue={(opt, val) => opt.lineItemId === val.lineItemId}
            />
          )}

          {/* Supplier scope -> Autocomplete from unique suppliers */}
          {adj.scope === 'SUPPLIER' && !isGlobalType(adj.type) && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={uniqueSuppliers}
              freeSolo
              value={uniqueSuppliers.find((s) => s.supplierId === adj.target) || (adj.target ? { supplierId: adj.target, name: adj.target } as SupplierOption : null)}
              onChange={(_, opt) => {
                if (typeof opt === 'string') {
                  onUpdate(adj.id, { target: opt });
                } else {
                  onUpdate(adj.id, { target: opt?.supplierId || '' });
                }
              }}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
              renderInput={(params) => (
                <TextField {...params} label="Target Supplier" placeholder="Select or type..." />
              )}
              isOptionEqualToValue={(opt, val) => {
                if (typeof opt === 'string' || typeof val === 'string') return opt === val;
                return opt.supplierId === val.supplierId;
              }}
            />
          )}

          {/* Material category -> free text */}
          {adj.scope === 'MATERIAL_CATEGORY' && !isGlobalType(adj.type) && (
            <TextField
              size="small"
              label="Material Category"
              placeholder="e.g., Aluminum, Steel"
              value={adj.target}
              onChange={(e) => onUpdate(adj.id, { target: e.target.value })}
              sx={{ minWidth: 160 }}
            />
          )}

          <TextField
            size="small"
            label="Value"
            type="number"
            value={adj.value}
            onChange={(e) => onUpdate(adj.id, { value: parseFloat(e.target.value) || 0 })}
            sx={{ width: 100 }}
          />
        </>
      )}

      <IconButton size="small" onClick={() => onRemove(adj.id)} color="error" sx={{ mt: 0.5 }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// ============================================
// METRIC CARD
// ============================================

interface MetricCardProps {
  label: string;
  baseline: number;
  simulated: number;
  delta: number;
  deltaPercent?: number;
  format: 'currency' | 'percent' | 'days' | 'count';
  invertColor?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  baseline,
  simulated,
  delta,
  deltaPercent,
  format,
  invertColor,
}) => {
  const formatValue = (v: number) => {
    switch (format) {
      case 'currency': return formatCurrency(v);
      case 'percent': return `${v.toFixed(1)}%`;
      case 'days': return `${v.toFixed(1)}d`;
      case 'count': return v.toString();
      default: return v.toString();
    }
  };

  const isPositive = delta > 0;
  const isBad = invertColor ? isPositive : !isPositive;
  const color = delta === 0 ? 'text.secondary' : isBad ? 'error.main' : 'success.main';

  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={600}>
        {formatValue(simulated)}
      </Typography>
      {delta !== 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isBad ? (
            <TrendingUpIcon sx={{ fontSize: 14, color }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 14, color }} />
          )}
          <Typography variant="caption" color={color} fontWeight={500}>
            {delta > 0 ? '+' : ''}{format === 'currency' ? formatCurrency(delta) : delta.toFixed(1)}
            {deltaPercent != null ? ` (${deltaPercent > 0 ? '+' : ''}${deltaPercent.toFixed(1)}%)` : ''}
          </Typography>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        was {formatValue(baseline)}
      </Typography>
    </Box>
  );
};

export default WhatIfSimulator;
